import { useState, useCallback } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Loader2, History, X, FileText, Receipt, FileSpreadsheet, Printer, Mail, ChevronDown, ChevronRight,
  CheckCircle2, Circle, Activity, User, CreditCard, Flag, Car, Fuel, Gauge, AlertTriangle, TrendingUp, Zap, Clock,
} from "lucide-react";
import { fmtDate, to12h } from "@/lib/datetime";
import MissionControlActions from "@/components/admin/MissionControlActions";
import ReconciliationFixCard from "@/components/admin/ReconciliationFixCard";
import HamptonPaymentLedgerFixCard from "@/components/admin/HamptonPaymentLedgerFixCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Color coding: Paid=green, Partial=orange, Unpaid/BalanceDue=red, Completed=blue, Refunded=purple
const payStyle = (paid, balance, lifecycle) => {
  if (lifecycle === "closed") return { label: "COMPLETED", cls: "text-blue-400 border-blue-400/50 bg-blue-400/10" };
  if (balance > 0.01) return paid > 0.01
    ? { label: "PARTIAL", cls: "text-orange-400 border-orange-400/50 bg-orange-400/10" }
    : { label: "UNPAID", cls: "text-red-400 border-red-400/50 bg-red-400/10" };
  return { label: "PAID", cls: "text-emerald-400 border-emerald-400/50 bg-emerald-400/10" };
};

function printHtml(title, inner) {
  const w = window.open("", "_blank", "width=760,height=900");
  if (!w) { toast.error("Allow pop-ups to view/print/download."); return; }
  const safeTitle = String(title ?? "").replace(/[&<>"']/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" }[c]));
  w.document.title = safeTitle;
  const style = w.document.createElement("style");
  style.textContent = "body{font-family:Arial,sans-serif;margin:32px;color:#111}button{margin:0 8px 16px 0;padding:8px 16px;background:#F2B705;border:none;border-radius:6px;font-weight:700;cursor:pointer}@media print{button{display:none}}";
  w.document.head.appendChild(style);
  const btn = w.document.createElement("button");
  btn.textContent = "🖨 Print / Download PDF";
  btn.onclick = () => w.print();
  const wrap = w.document.createElement("div");
  wrap.innerHTML = inner;
  w.document.body.appendChild(btn);
  w.document.body.appendChild(wrap);
}

export default function RentalTimeline({ booking, token }) {
  const [open, setOpen] = useState(false);
  const [tl, setTl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState("original");
  const [hist, setHist] = useState(null);
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/bookings/${booking.id}/rental-timeline`, auth);
      setTl(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not load rental history."); }
    finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [booking.id]);

  const toggle = () => { const n = !open; setOpen(n); if (n && !tl) load(); };

  const viewAgreement = (id) => id ? window.open(`${BACKEND}/api/agreements/${id}/pdf?auth=${token}`, "_blank") : toast.error("No agreement on file yet.");

  const openDoc = async (type, number) => {
    if (!number) { toast.error(`No ${type} on file${type === "receipt" ? " (no payment recorded)" : ""}.`); return; }
    try {
      const { data } = await axios.get(`${API}/${type}s/${number}`, auth);
      printHtml(`${type === "invoice" ? "Invoice" : "Receipt"} ${number}`, type === "invoice" ? invoiceHtml(data) : receiptHtml(data));
    } catch { toast.error(`Could not open ${type} ${number}.`); }
  };

  const emailDoc = async (type, number, audience) => {
    if (!number) { toast.error(`No ${type} to email.`); return; }
    try {
      const { data } = await axios.post(`${API}/${type}s/${number}/email`, { audience }, auth);
      toast.success(`${type} ${number} emailed to ${data.emailed_to.join(", ")}.`);
    } catch (e) { toast.error(e?.response?.data?.detail || `Could not email ${type}.`); }
  };

  const toggleFlag = async (key, on) => {
    try {
      const { data } = await axios.post(`${API}/bookings/${booking.id}/flags`, { key, on }, auth);
      setTl((p) => ({ ...p, flags: data.flags }));
    } catch { toast.error("Could not update flag."); }
  };

  const openHistory = async () => {
    setHist({ loading: true });
    try {
      const { data } = await axios.get(`${API}/customers/${encodeURIComponent(tl.customer.email)}/history`, auth);
      setHist({ loading: false, data });
    } catch { setHist(null); toast.error("Could not load customer history."); }
  };

  const chapKey = (n) => (n === "original" ? "original" : `ext-${n}`);
  const clickChapter = (k) => setExpanded((p) => (p === k ? null : k));

  return (
    <>
      <button data-testid={`rental-timeline-btn-${booking.id}`} onClick={toggle}
        className="text-xs flex items-center gap-1.5 border border-white/20 text-white/70 px-3 py-1.5 rounded hover:bg-white/10 hover:text-white transition-colors">
        <History size={13} /> Rental History
      </button>

      {open && (
        <div data-testid={`rental-timeline-panel-${booking.id}`} className="w-full mt-2 rounded-lg border border-white/15 bg-[#0A0A0A] p-4 space-y-4 max-h-[82vh] overflow-y-auto">
          <div className="flex items-center justify-between sticky top-0 bg-[#0A0A0A] pb-2 z-10">
            <span className="text-sm font-semibold text-white flex items-center gap-1.5"><History size={15} className="text-[#F2B705]" /> Rental Timeline & Ledger</span>
            <div className="flex items-center gap-2">
              {tl && <button data-testid={`customer-history-btn-${booking.id}`} onClick={openHistory}
                className="text-[11px] flex items-center gap-1 border border-white/20 text-white/70 px-2 py-1 rounded hover:bg-[#F2B705] hover:text-black transition-colors"><User size={12} /> Customer History</button>}
              <button onClick={() => setOpen(false)} className="text-white/40 hover:text-white"><X size={16} /></button>
            </div>
          </div>

          {loading && <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="animate-spin" size={14} /> Loading complete history…</div>}

          {tl && (
            <>
              {/* Quick Actions toolbar */}
              <MissionControlActions booking={booking} tl={tl} token={token} onSaved={load} />

              {/* Next Action */}
              <NextAction na={tl.next_action} testid={`next-action-${booking.id}`} />

              {/* KPI strip */}
              <KpiStrip kpis={tl.kpis} testid={`kpi-strip-${booking.id}`} />

              {/* Snapshots */}
              <div className="grid md:grid-cols-2 gap-3">
                <VehicleSnapshot v={tl.vehicle_card} testid={`vehicle-snapshot-${booking.id}`} />
                <CustomerSnapshot c={tl.customer_card} testid={`customer-snapshot-${booking.id}`} />
              </div>

              {/* Health + Stats */}
              <div className="grid md:grid-cols-2 gap-3">
                <HealthCard health={tl.health} testid={`rental-health-${booking.id}`} />
                <StatsCard stats={tl.stats} testid={`rental-stats-${booking.id}`} />
              </div>

              {/* Progress bar */}
              <ProgressBar steps={tl.progress} testid={`rental-progress-${booking.id}`} />

              {/* Recovery (auto-opens when overdue) */}
              {tl.recovery?.is_overdue && <RecoveryPanel rec={tl.recovery} testid={`recovery-panel-${booking.id}`} />}

              {/* Internal flags */}
              <Flags catalog={tl.flag_catalog} active={tl.flags} onToggle={toggleFlag} testid={`internal-flags-${booking.id}`} />

              {/* Chapters (collapsible) */}
              <div className="space-y-2">
                <Chapter data={{ ...tl.original, title: "Original Rental", accent: "#F2B705", days: tl.original.days }}
                  lifecycle={tl.stats.current_status?.toLowerCase()} expanded={expanded === "original"} onClick={() => clickChapter("original")}
                  testid={`chapter-original-${booking.id}`} onViewAgreement={viewAgreement} onOpenDoc={openDoc} onEmail={emailDoc} />
                {(tl.extensions || []).map((e) => (
                  <Chapter key={e.number}
                    data={{ ...e, title: `Extension #${e.number}`, accent: "#60a5fa", charges: e.total,
                      meta: `Requested ${e.request_date || "—"} · Approved ${e.approved_date || "—"}${e.approved_by ? " by " + e.approved_by : ""}` }}
                    expanded={expanded === chapKey(e.number)} onClick={() => clickChapter(chapKey(e.number))}
                    testid={`chapter-ext-${e.number}-${booking.id}`} onViewAgreement={viewAgreement} onOpenDoc={openDoc} onEmail={emailDoc} />
                ))}
              </div>

              {/* Payment History */}
              <PaymentHistory payments={tl.payments} testid={`payment-history-${booking.id}`} />

              {/* Activity Feed */}
              <ActivityFeed feed={tl.activity_feed} testid={`activity-feed-${booking.id}`} />

              {/* Profit Dashboard (management only) */}
              <ProfitDashboard profit={tl.profit} vehicleId={tl.vehicle_card?.id}
                token={token} onSaved={load} testid={`profit-dashboard-${booking.id}`} />

              {/* Master Summary */}
              <div data-testid={`timeline-summary-${booking.id}`} className="rounded-lg border border-[#F2B705]/30 bg-[#0C0C0C] p-4">
                <div className="text-xs uppercase tracking-widest text-[#F2B705] mb-3">Master Rental Summary</div>
                <div className="space-y-1.5 text-sm">
                  <SumRow label="Original Rental Total" value={money(tl.summary.original_total)} />
                  {tl.summary.extension_totals.map((x) => <SumRow key={x.number} label={`Extension #${x.number} Total`} value={money(x.total)} />)}
                  {tl.summary.damage_charges_total > 0 && <SumRow label="Damage Charges" value={money(tl.summary.damage_charges_total)} />}
                  {tl.summary.total_discount > 0 && <SumRow label="Discounts" value={`-${money(tl.summary.total_discount)}`} tone="emerald" />}
                  <div className="border-t border-white/10 my-1" />
                  <SumRow label="Total Extensions" value={`${tl.summary.total_extensions} extension(s)`} />
                  <SumRow label="Total Extension Charges" value={money(tl.summary.extensions_total)} />
                  <div className="border-t border-white/10 my-1" />
                  <SumRow label="Lifetime Rental Total" value={money(tl.summary.lifetime_total)} bold />
                  <SumRow label="Total Amount Paid" value={money(tl.summary.total_paid)} tone="emerald" />
                  <SumRow label="Remaining Balance" value={money(tl.summary.remaining_balance)} tone={tl.summary.remaining_balance > 0.01 ? "red" : "emerald"} bold />
                  <div className="border-t border-white/10 my-1" />
                  <SumRow label="Total Rental Days" value={`${tl.summary.total_days} day(s)`} />
                  <SumRow label="Current Return Date" value={tl.summary.current_return_date ? fmtDate(tl.summary.current_return_date) : "—"} />
                  <SumRow label="Current Return Time" value={tl.summary.current_return_time ? to12h(tl.summary.current_return_time) : "—"} />
                </div>
              </div>

              {/* Owner-only narrowly-scoped reconciliation correction (Addison/Hampton only) */}
              <ReconciliationFixCard bookingId={booking.id} token={token} onApplied={load} />
              {/* Separate, distinctly labeled Martrell payment-ledger money correction — never mixed with the timeline-gap card above */}
              <HamptonPaymentLedgerFixCard bookingId={booking.id} token={token} onApplied={load} />
            </>
          )}
        </div>
      )}

      {hist && <CustomerHistoryModal state={hist} onClose={() => setHist(null)} />}
    </>
  );
}

/* ---------------- Cards ---------------- */
const NA_STYLE = {
  green: { bg: "bg-emerald-500/10 border-emerald-500/40", dot: "bg-emerald-400", text: "text-emerald-300" },
  yellow: { bg: "bg-yellow-500/10 border-yellow-500/40", dot: "bg-yellow-400", text: "text-yellow-200" },
  orange: { bg: "bg-orange-500/10 border-orange-500/40", dot: "bg-orange-400", text: "text-orange-200" },
  red: { bg: "bg-red-500/10 border-red-500/40", dot: "bg-red-400", text: "text-red-300" },
};
function NextAction({ na, testid }) {
  const s = NA_STYLE[na.level] || NA_STYLE.green;
  return (
    <div data-testid={testid} className={`rounded-lg border ${s.bg} p-3 flex items-center gap-3`}>
      <span className={`h-3 w-3 rounded-full ${s.dot} shrink-0 animate-pulse`} />
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-white/40 flex items-center gap-1"><Zap size={11} /> Next Action</div>
        <div className={`text-sm font-semibold ${s.text}`}>{na.text}</div>
      </div>
    </div>
  );
}

function KpiStrip({ kpis, testid }) {
  const items = [
    ["Outstanding", money(kpis.outstanding_balance), kpis.outstanding_balance > 0.01 ? "red" : "emerald"],
    ["Rental Value", money(kpis.rental_value)],
    ["Days Remaining", kpis.days_remaining === null ? "—" : `${kpis.days_remaining}`, kpis.days_remaining < 0 ? "red" : ""],
    ["Extensions", `${kpis.extensions}`],
    ["Total Payments", money(kpis.total_payments), "emerald"],
    ["Customer LTV", money(kpis.customer_ltv)],
    ["Profit Est.", money(kpis.profit_estimate)],
    ["Stage", kpis.current_stage],
  ];
  return (
    <div data-testid={testid} className="grid grid-cols-2 sm:grid-cols-4 gap-2">
      {items.map(([k, v, tone], i) => (
        <div key={i} className="bg-[#0C0C0C] border border-white/10 rounded p-2">
          <div className="text-[9px] uppercase tracking-wide text-white/40">{k}</div>
          <div className={`text-sm font-bold tabular-nums ${tone === "red" ? "text-red-400" : tone === "emerald" ? "text-emerald-400" : "text-white/90"}`}>{v}</div>
        </div>
      ))}
    </div>
  );
}

const initials = (n) => (n || "?").split(" ").map((x) => x[0]).slice(0, 2).join("").toUpperCase();
function VehicleSnapshot({ v, testid }) {
  const rows = [["Plate", v.license_plate || "—"], ["VIN", v.vin || "—"], ["Odometer", v.odometer ? `${v.odometer.toLocaleString()} mi` : "—"],
    ["Fuel Out", v.fuel_out], ["Current Fuel", v.fuel_current],
    ["Oil Change Due In", v.oil_due_in_miles != null ? `${v.oil_due_in_miles.toLocaleString()} mi` : "—"],
    ["Registration", v.registration_expiry || "—"], ["Status", v.status || "—"]];
  return (
    <div data-testid={testid} className="rounded-lg border border-white/15 bg-[#0C0C0C] p-3">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5"><Car size={12} /> Vehicle Snapshot</div>
      <div className="flex gap-3">
        {v.photo ? <img src={v.photo} alt="vehicle" className="h-16 w-24 object-cover rounded border border-white/10 shrink-0" />
          : <div className="h-16 w-24 rounded border border-white/10 bg-white/5 flex items-center justify-center text-white/30"><Car size={20} /></div>}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white capitalize">{[v.year, v.make, v.model].filter(Boolean).join(" ") || "Vehicle"}</div>
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
        {rows.map(([k, val], i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <span className="text-white/45">{k}</span>
            <span className={`font-medium truncate ${val === "Not recorded" ? "text-white/35" : "text-white/80"} ${k === "Status" ? "capitalize" : ""}`}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function CustomerSnapshot({ c, testid }) {
  const [imgOk, setImgOk] = useState(true);
  const photo = c.photo && !/^https?:\/\//.test(c.photo) ? `${BACKEND}${c.photo.startsWith("/") ? "" : "/"}${c.photo}` : c.photo;
  const rows = [["Rating", `${c.rating || 0}/5`], ["Total Rentals", c.total_rentals], ["Lifetime Revenue", money(c.lifetime_revenue)],
    ["Late Returns", c.late_returns], ["Damage Claims", c.damage_claims], ["Credits Owed", c.credits_owed],
    ["Birthday", c.birthday || "—"], ["Preferred Vehicle", c.preferred_vehicle || "—"]];
  return (
    <div data-testid={testid} className="rounded-lg border border-white/15 bg-[#0C0C0C] p-3">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5"><User size={12} /> Customer Snapshot</div>
      <div className="flex items-center gap-3">
        {photo && imgOk ? <img src={photo} alt="customer" onError={() => setImgOk(false)} className="h-14 w-14 object-cover rounded-full border border-white/10 shrink-0" />
          : <div className="h-14 w-14 rounded-full bg-[#F2B705]/20 text-[#F2B705] flex items-center justify-center font-bold text-sm shrink-0">{initials(c.name)}</div>}
        <div className="min-w-0">
          <div className="text-sm font-semibold text-white truncate">{c.name}</div>
          {c.blocked
            ? <span className="text-[10px] font-bold uppercase text-red-400 border border-red-400/50 rounded px-1.5 py-0.5">🚫 On Block List</span>
            : <span className="text-[10px] uppercase text-emerald-400/80 border border-emerald-400/30 rounded px-1.5 py-0.5">Good Standing</span>}
        </div>
      </div>
      <div className="grid grid-cols-2 gap-x-4 gap-y-1 mt-2 text-xs">
        {rows.map(([k, val], i) => (
          <div key={i} className="flex items-center justify-between gap-2">
            <span className="text-white/45">{k}</span>
            <span className={`font-medium ${k === "Credits Owed" && c.credits_owed > 0 ? "text-[#F2B705]" : "text-white/80"}`}>{val}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function RecoveryPanel({ rec, testid }) {
  const rows = [["Days Overdue", rec.days_overdue], ["Calls Made", rec.calls_made], ["SMS Sent", rec.sms_sent],
    ["Emails Sent", rec.emails_sent], ["Demand Notice", rec.demand_sent ? "Sent" : "Not sent"],
    ["Recovery Status", rec.recovery_status], ["Repo Status", rec.repo_status], ["Collection Status", rec.collection_status]];
  return (
    <div data-testid={testid} className="rounded-lg border border-red-500/40 bg-red-500/5 p-3">
      <div className="text-xs uppercase tracking-widest text-red-300 mb-2 flex items-center gap-1.5"><AlertTriangle size={13} /> Collections & Recovery</div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-1 text-xs">
        {rows.map(([k, val], i) => (
          <div key={i} className="flex flex-col">
            <span className="text-white/45">{k}</span>
            <span className="font-semibold text-white/85 capitalize">{val}</span>
          </div>
        ))}
      </div>
      {rec.escalation_stopped && <div className="mt-2 text-[11px] text-orange-300">⚠ Auto-escalation texts are currently stopped for this rental.</div>}
    </div>
  );
}

function ActivityFeed({ feed, testid }) {
  const fmtTs = (ts) => {
    try { const d = new Date(ts); return `${fmtDate(ts)} · ${d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", timeZone: "America/Chicago" })}`; }
    catch { return ts; }
  };
  return (
    <div data-testid={testid} className="rounded-lg border border-white/15 bg-[#0C0C0C] p-3">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5"><Clock size={12} /> Activity Feed</div>
      {feed.length === 0 ? <p className="text-white/35 text-xs">No activity yet.</p> : (
        <div className="space-y-2 max-h-64 overflow-y-auto pr-1">
          {feed.map((f, i) => (
            <div key={i} className="flex gap-2 text-xs">
              <span className="h-1.5 w-1.5 rounded-full bg-[#F2B705] mt-1.5 shrink-0" />
              <div className="min-w-0">
                <div className="text-white/80 font-medium">{f.label}{f.detail ? <span className="text-white/45 font-normal"> · {f.detail}</span> : null}</div>
                <div className="text-white/30 text-[10px]">{fmtTs(f.ts)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ProfitDashboard({ profit, vehicleId, token, onSaved, testid }) {
  const [show, setShow] = useState(false);
  const [edit, setEdit] = useState(false);
  const [life, setLife] = useState(profit.useful_life_months || 60);
  const [manual, setManual] = useState(profit.manual_daily_cost || "");
  const p = profit;
  const rows = [
    ["Rental Revenue", money(p.rental_revenue)], ["Damage Waiver Revenue", money(p.damage_waiver_revenue)],
    ["Insurance Revenue", money(p.insurance_revenue)], ["Other Fees", money(p.other_fees)],
    ["Discounts", `-${money(p.discounts)}`], ["Estimated Daily Ownership Cost", money(p.daily_ownership_cost)],
    ["Estimated Vehicle Cost (this rental)", money(p.vehicle_cost_rental)],
  ];
  const save = async () => {
    if (!vehicleId) { toast.error("Vehicle not matched — cannot save cost settings."); return; }
    try {
      await axios.patch(`${API}/vehicles/${vehicleId}/cost-settings`, { useful_life_months: parseInt(life) || 60, manual_daily_cost: parseFloat(manual) || 0 }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success("Vehicle cost settings saved."); setEdit(false); onSaved?.();
    } catch { toast.error("Could not save cost settings."); }
  };
  return (
    <div data-testid={testid} className="rounded-lg border border-purple-500/30 bg-purple-500/5 overflow-hidden">
      <button onClick={() => setShow((s) => !s)} className="w-full flex items-center justify-between p-3 hover:bg-white/5 transition-colors">
        <span className="text-xs uppercase tracking-widest text-purple-300 flex items-center gap-1.5"><TrendingUp size={13} /> Profit Dashboard <span className="normal-case tracking-normal text-white/30">(management only · estimated)</span></span>
        {show ? <ChevronDown size={15} className="text-white/50" /> : <ChevronRight size={15} className="text-white/50" />}
      </button>
      {show && (
        <div className="px-3 pb-3 space-y-1.5 text-sm border-t border-white/10 pt-2">
          {rows.map(([k, v], i) => <SumRow key={i} label={k} value={v} />)}
          <div className="border-t border-white/10 my-1" />
          <SumRow label="Estimated Gross Profit" value={money(p.estimated_gross_profit)} tone={p.estimated_gross_profit >= 0 ? "emerald" : "red"} bold />
          <SumRow label="Estimated Net Profit" value={money(p.estimated_net_profit)} tone={p.estimated_net_profit >= 0 ? "emerald" : "red"} bold />
          <div className="border-t border-white/10 my-1" />
          <SumRow label="Revenue / Day" value={money(p.revenue_per_day)} />
          <SumRow label="Revenue / Week" value={money(p.revenue_per_week)} />
          <SumRow label="Revenue / Month" value={money(p.revenue_per_month)} />
          <div className="text-[10px] text-white/35 mt-1">
            {p.is_manual_cost ? "Using manual daily ownership cost." : `Estimated from purchase price ${money(p.purchase_price)} ÷ ${p.useful_life_months} months.`}
          </div>
          {!edit ? (
            <button data-testid={`${testid}-cost-settings`} onClick={() => setEdit(true)} className="text-[11px] text-purple-300 hover:text-white underline mt-1">Vehicle Cost Settings</button>
          ) : (
            <div className="mt-2 flex flex-wrap items-end gap-3 border-t border-white/10 pt-2">
              <div><label className="text-[10px] text-white/45 block">Useful Life (months)</label><input type="number" value={life} onChange={(e) => setLife(e.target.value)} className="w-20 bg-[#0C0C0C] border border-white/15 rounded px-2 py-1 text-xs text-white" /></div>
              <div><label className="text-[10px] text-white/45 block">Manual Daily Cost ($)</label><input type="number" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="auto" className="w-24 bg-[#0C0C0C] border border-white/15 rounded px-2 py-1 text-xs text-white placeholder:text-white/30" /></div>
              <button onClick={save} className="bg-[#F2B705] text-black text-xs font-bold px-3 py-1.5 rounded">Save</button>
              <button onClick={() => setEdit(false)} className="text-white/40 text-xs">Cancel</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

const BAND = { green: "text-emerald-400 border-emerald-400/40", orange: "text-orange-400 border-orange-400/40", red: "text-red-400 border-red-400/40" };
function HealthCard({ health, testid }) {
  return (
    <div data-testid={testid} className={`rounded-lg border bg-[#0C0C0C] p-3 ${BAND[health.band] || "border-white/15"}`}>
      <div className="flex items-center justify-between">
        <span className="text-xs uppercase tracking-widest text-white/50 flex items-center gap-1.5"><Activity size={13} /> Rental Health</span>
        <span className={`font-display font-bold text-2xl ${BAND[health.band]?.split(" ")[0]}`}>{health.score}<span className="text-sm text-white/40">/100</span></span>
      </div>
      <div className="mt-2 space-y-1">
        {health.checks.map((c, i) => (
          <div key={i} className="flex items-center gap-2 text-xs">
            {c.ok ? <CheckCircle2 size={13} className="text-emerald-400" /> : <span className={c.warn ? "text-orange-400" : "text-red-400"}>{c.warn ? "⚠" : "✕"}</span>}
            <span className={c.ok ? "text-white/70" : c.warn ? "text-orange-300" : "text-red-300"}>{c.label}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function StatsCard({ stats, testid }) {
  const rows = [
    ["Original Rental", `${stats.original_days} Days`], ["Extensions", `${stats.extensions}`],
    ["Total Rental Days", `${stats.total_days}`], ["Lifetime Revenue", money(stats.lifetime_revenue)],
    ["Outstanding Balance", money(stats.outstanding_balance)], ["Current Status", stats.current_status || "—"],
    ["Current Return", `${stats.current_return_date ? fmtDate(stats.current_return_date) : "—"}${stats.current_return_time ? " @ " + to12h(stats.current_return_time) : ""}`],
  ];
  return (
    <div data-testid={testid} className="rounded-lg border border-white/15 bg-[#0C0C0C] p-3">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2">Rental Stats</div>
      <div className="space-y-1">
        {rows.map(([k, v], i) => (
          <div key={i} className="flex items-center justify-between text-xs">
            <span className="text-white/45">{k}</span>
            <span className={`font-medium ${k === "Outstanding Balance" && stats.outstanding_balance > 0.01 ? "text-red-400" : "text-white/85"}`}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

function ProgressBar({ steps, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-white/15 bg-[#0C0C0C] p-3">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-3">Rental Progress</div>
      <div className="flex items-center flex-wrap gap-y-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-center">
            <div className="flex flex-col items-center gap-1 w-20 text-center">
              {s.done ? <CheckCircle2 size={18} className="text-emerald-400" /> : <Circle size={18} className="text-white/25" />}
              <span className={`text-[10px] leading-tight ${s.done ? "text-white/70" : "text-white/35"}`}>{s.label}</span>
            </div>
            {i < steps.length - 1 && <div className={`h-px w-4 ${steps[i + 1].done ? "bg-emerald-400/50" : "bg-white/15"}`} />}
          </div>
        ))}
      </div>
    </div>
  );
}

function Flags({ catalog, active, onToggle, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-white/15 bg-[#0C0C0C] p-3">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5"><Flag size={12} /> Internal Flags <span className="text-white/25 normal-case tracking-normal">(management only)</span></div>
      <div className="flex flex-wrap gap-1.5">
        {catalog.map((f) => {
          const on = active.includes(f.key);
          return (
            <button key={f.key} data-testid={`${testid}-${f.key}`} onClick={() => onToggle(f.key, !on)}
              className={`text-[11px] px-2.5 py-1 rounded-full border transition-colors ${on ? "bg-[#F2B705] text-black border-[#F2B705] font-semibold" : "border-white/20 text-white/50 hover:text-white hover:border-white/40"}`}>
              {f.icon} {f.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}

function PaymentHistory({ payments, testid }) {
  return (
    <div data-testid={testid} className="rounded-lg border border-white/15 bg-[#0C0C0C] p-3">
      <div className="text-xs uppercase tracking-widest text-white/50 mb-2 flex items-center gap-1.5"><CreditCard size={12} /> Payment History</div>
      {payments.length === 0 ? <p className="text-white/35 text-xs">No payments recorded yet.</p> : (
        <div className="space-y-1">
          {payments.map((p, i) => (
            <div key={i} className="flex items-center justify-between text-xs border-b border-white/5 pb-1">
              <span className="text-white/55 w-24">{p.date ? fmtDate(p.date) : "—"}</span>
              <span className="text-white/70 flex-1">{p.method}{p.number ? ` · ${p.number}` : ""}</span>
              <span className="text-white/85 tabular-nums w-20 text-right">{money(p.amount)}</span>
              <span className="text-emerald-400 w-12 text-right">{p.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ---------------- Chapter (collapsible) ---------------- */
function Chapter({ data, lifecycle, expanded, onClick, testid, onViewAgreement, onOpenDoc, onEmail }) {
  const st = payStyle(data.amount_paid, data.balance, lifecycle);
  const ag = data.agreement || {};
  return (
    <div data-testid={testid} className="rounded-lg border border-white/10 bg-[#0C0C0C] overflow-hidden">
      <button onClick={onClick} className="w-full flex items-center justify-between gap-2 p-3 hover:bg-white/5 transition-colors text-left">
        <div className="flex items-center gap-2 min-w-0">
          {expanded ? <ChevronDown size={15} className="text-white/50 shrink-0" /> : <ChevronRight size={15} className="text-white/50 shrink-0" />}
          <span className="h-2.5 w-2.5 rounded-full shrink-0" style={{ background: data.accent }} />
          <span className="text-sm font-semibold text-white shrink-0">{data.title}</span>
          <span className="text-xs text-white/45 truncate">{data.start_date} → {data.end_date} · {data.days} day(s)</span>
        </div>
        <span data-testid={`${testid}-status`} className={`text-[10px] font-bold uppercase tracking-wide border rounded px-2 py-0.5 shrink-0 ${st.cls}`}>
          {st.label}{data.balance > 0.01 ? ` · ${money(data.balance)}` : ""}
        </span>
      </button>

      {expanded && (
        <div className="px-3 pb-3 border-t border-white/10 pt-3">
          {data.meta && <div className="text-white/40 text-[11px] mb-2">{data.meta}</div>}

          <div className="flex flex-wrap gap-2 text-[11px]">
            <span className="border border-white/15 rounded px-2 py-0.5 text-white/70">Agreement <b className="text-white/90">{data.agreement_number || "—"}</b></span>
            <span className="border border-white/15 rounded px-2 py-0.5 text-white/70">Invoice <b className="text-white/90">{data.invoice_number || "—"}</b></span>
            <span className="border border-white/15 rounded px-2 py-0.5 text-white/70">Receipt <b className="text-white/90">{data.receipt_number || "—"}</b></span>
          </div>

          {/* Agreement status */}
          <div className="mt-2 rounded border border-white/10 bg-[#0A0A0A] p-2 text-[11px]">
            <div className={`font-semibold ${ag.signed ? "text-emerald-400" : "text-orange-400"}`}>{ag.signed ? "✔ Agreement Signed" : ag.exists ? "⚠ Awaiting Signature" : "No agreement"}</div>
            {ag.signed && (
              <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 mt-1 text-white/55">
                {ag.signed_date && <span>Date: {ag.signed_date}</span>}
                {ag.signed_time && <span>Time: {to12h(ag.signed_time)}</span>}
                {ag.ip_address && <span>IP: {ag.ip_address}</span>}
                <span>Email verified: {ag.email_verified ? "Yes" : "No"}</span>
                <span>SMS verified: {ag.sms_verified ? "Yes" : "No"}</span>
              </div>
            )}
          </div>

          {/* Details grid */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 mt-3 text-xs">
            <Detail label="Daily rate" value={data.daily_rate ? money(data.daily_rate) : "—"} />
            <Detail label="Number of days" value={`${data.days} day(s)`} />
            <Detail label="Damage waiver" value={data.waiver_amount > 0 ? money(data.waiver_amount) : "None"} />
            <Detail label="Discounts" value={data.discount > 0 || data.discount_amount > 0 ? `-${money(data.discount || data.discount_amount)}` : "—"} />
            <Detail label="Amount paid" value={money(data.amount_paid)} tone="emerald" />
            <Detail label="Balance" value={money(data.balance)} tone={data.balance > 0.01 ? "red" : "emerald"} />
          </div>

          {/* Line items */}
          <div className="mt-3 space-y-1 border-t border-white/10 pt-2">
            {(data.line_items || []).map((it, i) => (
              <div key={i} className="flex items-center justify-between text-xs">
                <span className="text-white/60">{it.label}{it.detail ? <span className="text-white/35"> · {it.detail}</span> : null}</span>
                <span className={it.amount < 0 ? "text-emerald-400" : "text-white/80"}>{it.amount < 0 ? "-" : ""}{money(Math.abs(it.amount))}</span>
              </div>
            ))}
            <div className="flex items-center justify-between text-xs border-t border-white/10 pt-1 mt-1">
              <span className="text-white/70 font-semibold">Total</span><span className="text-white font-semibold">{money(data.charges)}</span>
            </div>
          </div>

          {/* Quick actions */}
          <div className="flex flex-wrap gap-1.5 mt-3">
            <Action testid={`${testid}-view-agreement`} icon={FileText} label="View Agreement" onClick={() => onViewAgreement(data.agreement_id)} />
            <Action testid={`${testid}-view-invoice`} icon={FileSpreadsheet} label="View Invoice" onClick={() => onOpenDoc("invoice", data.invoice_number)} />
            <Action testid={`${testid}-view-receipt`} icon={Receipt} label="View Receipt" onClick={() => onOpenDoc("receipt", data.receipt_number)} />
            <Action testid={`${testid}-print-receipt`} icon={Printer} label="Print Receipt" onClick={() => onOpenDoc("receipt", data.receipt_number)} />
            <Action testid={`${testid}-email-customer`} icon={Mail} label="Email Customer" onClick={() => onEmail("invoice", data.invoice_number, "customer")} />
            <Action testid={`${testid}-email-management`} icon={Mail} label="Email Management" onClick={() => onEmail("invoice", data.invoice_number, "management")} />
          </div>
        </div>
      )}
    </div>
  );
}

const Detail = ({ label, value, tone }) => (
  <div className="flex items-center justify-between gap-2">
    <span className="text-white/45">{label}</span>
    <span className={`font-medium ${tone === "emerald" ? "text-emerald-400" : tone === "red" ? "text-red-400" : "text-white/80"}`}>{value}</span>
  </div>
);
const Action = ({ icon: Icon, label, onClick, testid }) => (
  <button data-testid={testid} onClick={onClick}
    className="text-[11px] flex items-center gap-1 border border-white/15 text-white/70 px-2 py-1 rounded hover:bg-[#F2B705] hover:text-black hover:border-[#F2B705] transition-colors">
    <Icon size={11} /> {label}
  </button>
);
const SumRow = ({ label, value, tone, bold }) => (
  <div className="flex items-center justify-between">
    <span className={`${bold ? "text-white font-semibold" : "text-white/55"}`}>{label}</span>
    <span className={`tabular-nums ${bold ? "font-bold text-base" : ""} ${tone === "emerald" ? "text-emerald-400" : tone === "red" ? "text-red-400" : "text-white/85"}`}>{value}</span>
  </div>
);

/* ---------------- Customer History modal ---------------- */
function CustomerHistoryModal({ state, onClose }) {
  const d = state.data;
  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/70 p-4" onClick={onClose}>
      <div data-testid="customer-history-modal" onClick={(e) => e.stopPropagation()}
        className="w-full max-w-2xl max-h-[85vh] overflow-y-auto rounded-xl border border-white/15 bg-[#0A0A0A] p-5">
        <div className="flex items-center justify-between mb-4">
          <span className="text-sm font-semibold text-white flex items-center gap-2"><User size={16} className="text-[#F2B705]" /> Customer History{d ? ` — ${d.email}` : ""}</span>
          <button onClick={onClose} className="text-white/40 hover:text-white"><X size={18} /></button>
        </div>
        {state.loading || !d ? <div className="flex items-center gap-2 text-white/50 text-sm"><Loader2 className="animate-spin" size={14} /> Loading…</div> : (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
              {[["Total Rentals", d.count], ["Lifetime Revenue", money(d.lifetime_revenue)], ["Outstanding", money(d.outstanding)],
                ["Extensions", d.extensions], ["Damage Claims", d.damage_claims], ["Rating", `${d.profile?.rating || 0}/5`]].map(([k, v], i) => (
                <div key={i} className="bg-[#0C0C0C] border border-white/10 rounded p-2">
                  <div className="text-[10px] uppercase text-white/40">{k}</div>
                  <div className="text-sm font-semibold text-white/90">{v}</div>
                </div>
              ))}
            </div>
            <div className="text-xs uppercase tracking-widest text-white/40 mb-2">All Rentals</div>
            <div className="space-y-1 mb-4">
              {d.rentals.map((r) => (
                <div key={r.id} className="flex items-center justify-between text-xs bg-[#0C0C0C] border border-white/10 rounded px-3 py-2">
                  <span className="text-white/80">{r.vehicle}</span>
                  <span className="text-white/45">{r.pickup_date} → {r.return_date}</span>
                  <span className="text-white/60">{r.status}</span>
                  <span className={r.balance > 0.01 ? "text-red-400" : "text-emerald-400"}>{money(r.balance)} due</span>
                </div>
              ))}
            </div>
            {(d.notes?.length > 0 || d.credits?.length > 0) && (
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Notes ({d.notes?.length || 0})</div>
                  {(d.notes || []).slice(0, 5).map((n) => <div key={n.id} className="text-xs text-white/60 border-b border-white/5 py-1">{n.text || n.note}</div>)}
                </div>
                <div>
                  <div className="text-xs uppercase tracking-widest text-white/40 mb-1">Credits ({d.credits?.length || 0})</div>
                  {(d.credits || []).slice(0, 5).map((c) => <div key={c.id} className="text-xs text-white/60 border-b border-white/5 py-1">{c.label}</div>)}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

/* ---------------- Printable HTML ---------------- */
const biz = `<div style="font-size:12px;color:#666;">Charles Harold Washington Jr d.b.a Auto-Hire STL · St. Louis, MO · (314) 936-1837 · support@auto-hire.info</div>`;
function hdr(title, number) {
  return `<div style="border-bottom:2px solid #F2B705;padding-bottom:12px;margin-bottom:16px;">
    <div style="font-size:22px;font-weight:800;">Auto-Hire STL</div>${biz}
    <div style="margin-top:10px;font-size:18px;font-weight:700;">${title}<span style="float:right;font-size:13px;color:#666;">#${number}</span></div></div>`;
}
const m = (v) => `$${(parseFloat(v) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
function invoiceHtml(inv) {
  const li = (inv.line_items || []).map((x) => `<tr><td style="padding:6px 0;border-bottom:1px solid #eee;">${x.label}${x.detail ? `<div style="font-size:11px;color:#888;">${x.detail}</div>` : ""}</td><td style="padding:6px 0;border-bottom:1px solid #eee;text-align:right;">${m(x.amount)}</td></tr>`).join("");
  const c = inv.customer || {}, p = inv.period || {};
  return `${hdr("INVOICE", inv.number)}
    <table style="width:100%;font-size:13px;margin-bottom:14px;">
      <tr><td style="color:#555;">Bill To</td><td style="text-align:right;font-weight:600;">${c.name || ""}</td></tr>
      <tr><td style="color:#555;">Vehicle</td><td style="text-align:right;font-weight:600;">${inv.vehicle || ""}</td></tr>
      <tr><td style="color:#555;">Rental Period</td><td style="text-align:right;font-weight:600;">${p.start || ""} → ${p.end || ""}</td></tr>
      <tr><td style="color:#555;">Agreement</td><td style="text-align:right;font-weight:600;">${inv.agreement_number || "—"}</td></tr></table>
    <table style="width:100%;font-size:13px;">${li}</table>
    <table style="width:100%;font-size:13px;margin-top:10px;">
      <tr><td style="color:#555;">Subtotal</td><td style="text-align:right;">${m(inv.subtotal)}</td></tr>
      <tr><td style="color:#555;">Discount</td><td style="text-align:right;">-${m(inv.discount)}</td></tr>
      <tr><td style="color:#555;">Tax</td><td style="text-align:right;">${m(inv.tax)}</td></tr>
      <tr><td style="font-weight:700;">Total</td><td style="text-align:right;font-weight:700;">${m(inv.total)}</td></tr>
      <tr><td style="color:#555;">Amount Paid</td><td style="text-align:right;">${m(inv.amount_paid)}</td></tr>
      <tr><td style="font-weight:700;">Balance Due</td><td style="text-align:right;font-weight:700;">${m(inv.balance)}</td></tr></table>`;
}
function receiptHtml(r) {
  const c = r.customer || {}, p = r.period || {};
  return `${hdr("PAYMENT RECEIPT", r.number)}
    <table style="width:100%;font-size:13px;">
      <tr><td style="color:#555;">Received From</td><td style="text-align:right;font-weight:600;">${c.name || ""}</td></tr>
      <tr><td style="color:#555;">Vehicle</td><td style="text-align:right;font-weight:600;">${r.vehicle || ""}</td></tr>
      <tr><td style="color:#555;">Rental Period</td><td style="text-align:right;font-weight:600;">${p.start || ""} → ${p.end || ""}</td></tr>
      <tr><td style="color:#555;">Payment Method</td><td style="text-align:right;font-weight:600;">${r.method || "record"}</td></tr>
      <tr><td style="color:#555;">Invoice</td><td style="text-align:right;font-weight:600;">${r.invoice_number || "—"}</td></tr>
      <tr><td style="color:#555;">Agreement</td><td style="text-align:right;font-weight:600;">${r.agreement_number || "—"}</td></tr>
      <tr><td style="color:#555;">Date</td><td style="text-align:right;font-weight:600;">${(r.created_at || "").slice(0, 10)}</td></tr></table>
    <div style="margin-top:14px;padding:14px;background:#F7F7F7;border-radius:8px;text-align:center;">
      <div style="font-size:12px;color:#666;">Amount Paid</div>
      <div style="font-size:26px;font-weight:800;">${m(r.amount)}</div></div>
    <div style="margin-top:40px;border-top:1px solid #333;width:260px;padding-top:6px;font-size:12px;color:#555;">Customer Signature</div>
    <div style="margin-top:12px;font-size:11px;color:#999;">Thank you for renting with Auto-Hire STL.</div>`;
}
